import { cn as clsx } from '../lib/cn';
import { AlertTriangle, CheckCircle2, ChevronDown, Info, Loader2, Search, X, XCircle } from 'lucide-react';
import {
  type ButtonHTMLAttributes, Children, cloneElement, createContext, forwardRef, type InputHTMLAttributes, isValidElement, type ReactElement,
  type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, useCallback, useContext, useEffect, useId, useRef, useState,
} from 'react';
import { ApiError } from '../lib/api';

// ═══ Botões ═════════════════════════════════════════════════════════════════
type Variant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'success';
type Size = 'xs' | 'sm' | 'md' | 'lg';
const variants: Record<Variant, string> = {
  primary: 'bg-brand-700 text-white shadow-sm shadow-brand-900/20 hover:bg-brand-800 active:bg-brand-900 ring-1 ring-inset ring-white/10',
  secondary: 'bg-[var(--surface)] text-[var(--text)] shadow-sm ring-1 ring-inset ring-[var(--border-strong)] hover:bg-[var(--surface-2)]',
  soft: 'bg-brand-500/10 text-brand-800 hover:bg-brand-500/20 dark:text-brand-100',
  ghost: 'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
  // Contorno: com o primário vermelho (Ribeiro), um perigo sólido confundir-se-ia com a acção principal.
  danger: 'bg-[var(--surface)] text-red-700 shadow-sm ring-1 ring-inset ring-red-600/45 hover:bg-red-50 hover:ring-red-600 active:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40',
  success: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
};
const sizes: Record<Size, string> = {
  xs: 'h-7 px-2 text-xs gap-1 rounded-md',
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-9.5 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-xl',
};
// Botões só com ícone: medidas próprias, SEM padding horizontal. (Com px-3 e
// px-0 juntos ganhava o px-3 e o ícone encolhia para 8 px.)
const iconSizes: Record<Size, string> = {
  xs: 'size-7 rounded-md', sm: 'size-8 rounded-lg', md: 'size-9.5 rounded-lg', lg: 'size-11 rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean; icon?: ReactNode; iconOnly?: boolean }>(
  ({ variant = 'primary', size = 'md', loading, icon, iconOnly, className, children, disabled, type = 'button', ...p }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-all duration-150 select-none',
        'disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        iconOnly ? iconSizes[size] : sizes[size], '[&_svg]:shrink-0', variants[variant], className,
      )}
      {...p}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {!iconOnly && children}
    </button>
  ),
);

// ═══ Campos ═════════════════════════════════════════════════════════════════
const fieldCls = 'w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface)] px-3 h-9.5 text-sm shadow-xs transition placeholder:text-[var(--muted)] focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/15 disabled:bg-[var(--surface-2)] disabled:opacity-70 aria-[invalid=true]:border-red-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { leading?: ReactNode; trailing?: ReactNode }>(({ className, leading, trailing, ...p }, ref) => {
  if (!leading && !trailing) return <input ref={ref} className={clsx(fieldCls, className)} {...p} />;
  return (
    <div className="relative">
      {leading && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[var(--muted)]">{leading}</span>}
      <input ref={ref} className={clsx(fieldCls, leading && 'pl-9', trailing && 'pr-12', className)} {...p} />
      {trailing && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-[var(--muted)]">{trailing}</span>}
    </div>
  );
});
const chevron = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath d='M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z'/%3E%3C/svg%3E")`;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Lista fechada. Se o valor actual não estiver entre as opções (dado antigo,
 * importado, de uma lista que mudou), acrescenta-o: um <select> controlado com
 * um valor desconhecido MOSTRAVA a primeira opção mas guardava o outro valor —
 * o utilizador via uma coisa e gravava outra.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, style, children, value, ...p }, ref) => {
  const known = new Set<string>();
  Children.toArray(children).forEach((c) => {
    if (isValidElement(c) && c.type === 'option') {
      const op = c.props as { value?: unknown; children?: unknown };
      known.add(String(op.value ?? op.children));
    }
  });
  const v = value === undefined || value === null || Array.isArray(value) ? null : String(value);
  const missing = v !== null && v !== '' && !known.has(v);
  return (
    <select ref={ref} value={value} className={clsx(fieldCls, 'appearance-none bg-[length:1.1rem] bg-[right_0.6rem_center] bg-no-repeat pr-9', className)} style={{ backgroundImage: chevron, ...style }} {...p}>
      {children}
      {missing && <option value={v}>{UUID_RE.test(v) ? '(valor actual — já não disponível)' : v}</option>}
    </select>
  );
});

/** Texto livre com sugestões (unidades, bancos, províncias): a lista ajuda, não limita. */
export const SuggestInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { suggestions: readonly string[] }>(({ suggestions, ...p }, ref) => {
  const listId = useId();
  return (
    <>
      <Input ref={ref} list={listId} autoComplete="off" {...p} />
      <datalist id={listId}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
    </>
  );
});
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea ref={ref} className={clsx(fieldCls, 'h-auto min-h-20 py-2 leading-relaxed', className)} {...p} />
));

/**
 * Rótulo ligado por htmlFor/id; dica e erro por aria-describedby. Com o
 * controlo dentro do <label>, o nome acessível de um <select> incluía a opção
 * escolhida ("Tipo Serviço") — o teste de interface apanhou-o.
 */
export function Field({ label, error, hint, required, children, className }: { label: string; error?: string; hint?: string; required?: boolean; children: ReactNode; className?: string }) {
  const id = useId();
  const msg = error ?? hint;
  const childId = isValidElement(children) ? ((children.props as { id?: string }).id ?? id) : undefined;
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, { id: childId, ...(msg ? { 'aria-describedby': `${id}-msg` } : {}), ...(error ? { 'aria-invalid': true } : {}) })
    : children;
  return (
    <div className={clsx('space-y-1.5', className)}>
      <label htmlFor={childId} className="block text-[13px] font-medium text-[var(--text-2)]">{label}{required && <span className="ml-0.5 text-red-500" aria-hidden>*</span>}</label>
      {child}
      {msg && <span id={`${id}-msg`} className={clsx('block text-xs', error ? 'text-red-600 dark:text-red-400' : 'text-[var(--muted)]')}>{msg}</span>}
    </div>
  );
}

export function Checkbox({ label, checked, onChange, hint, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; hint?: string; disabled?: boolean }) {
  return (
    <label className={clsx('flex items-start gap-2.5 text-sm', disabled ? 'opacity-60' : 'cursor-pointer')}>
      <input type="checkbox" disabled={disabled} className="mt-0.5 size-4 rounded accent-[var(--color-brand-700)]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}{hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}</span>
    </label>
  );
}

/** Interruptor para opções ligado/desligado. */
export function Switch({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span>{label}{hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}</span>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}
        className={clsx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition', checked ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600')}>
        <span className={clsx('absolute top-0.5 size-4 rounded-full bg-white shadow transition-all', checked ? 'left-[18px]' : 'left-0.5')} />
      </button>
    </div>
  );
}

// ═══ Estrutura ══════════════════════════════════════════════════════════════
export function Card({ children, className, title, subtitle, actions, padded = true }: { children: ReactNode; className?: string; title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; padded?: boolean }) {
  return (
    <section className={clsx('surface min-w-0 rounded-2xl shadow-[0_1px_2px_rgb(16_24_40/0.04)]', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] px-5 py-3.5">
          <div>
            <h2 className="text-[15px] font-semibold">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-[var(--muted)]">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-semibold tracking-wider text-brand-700 uppercase dark:text-brand-500">{eyebrow}</div>}
        <h1 className="text-[26px] leading-tight font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[var(--muted)]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

type Tone = 'gray' | 'green' | 'amber' | 'red' | 'blue' | 'teal' | 'violet';
export function Badge({ children, tone = 'gray', dot }: { children: ReactNode; tone?: Tone; dot?: boolean }) {
  const t = {
    gray: 'bg-gray-500/10 text-gray-700 ring-gray-500/20 dark:text-gray-300',
    green: 'bg-emerald-500/10 text-emerald-700 ring-emerald-600/20 dark:text-emerald-300',
    amber: 'bg-amber-500/12 text-amber-800 ring-amber-600/25 dark:text-amber-300',
    red: 'bg-red-500/10 text-red-700 ring-red-600/20 dark:text-red-300',
    blue: 'bg-blue-500/10 text-blue-700 ring-blue-600/20 dark:text-blue-300',
    teal: 'bg-brand-500/10 text-brand-800 ring-brand-600/20 dark:text-brand-100',
    violet: 'bg-violet-500/10 text-violet-700 ring-violet-600/20 dark:text-violet-300',
  }[tone];
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset', t)}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}{children}
    </span>
  );
}

export function StatusBadge({ status, training }: { status: string; training?: boolean }) {
  if (status === 'draft') return <Badge tone="amber" dot>Rascunho</Badge>;
  if (status === 'voided' || status === 'cancelled') return <Badge tone="red" dot>{status === 'cancelled' ? 'Cancelado' : 'Anulado'}</Badge>;
  if (status === 'posted') return <Badge tone="green" dot>Lançada</Badge>;
  if (status === 'open') return <Badge tone="blue" dot>Aberto</Badge>;
  if (status === 'closed') return <Badge tone="gray" dot>Fechado</Badge>;
  return training ? <Badge tone="blue" dot>Formação</Badge> : <Badge tone="green" dot>Emitido</Badge>;
}

export function StatCard({ label, value, sub, icon, tone }: { label: string; value: ReactNode; sub?: ReactNode; icon?: ReactNode; tone?: 'warn' | 'good' }) {
  return (
    <div className="surface rounded-2xl p-5 shadow-[0_1px_2px_rgb(16_24_40/0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[13px] font-medium text-[var(--muted)]">{label}</div>
        {icon && <div className={clsx('rounded-lg p-2 [&>svg]:size-4', tone === 'warn' ? 'bg-red-500/10 text-red-600' : tone === 'good' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-brand-500/10 text-brand-700 dark:text-brand-500')}>{icon}</div>}
      </div>
      <div className={clsx('mt-2 text-2xl font-bold tracking-tight tabular-nums', tone === 'warn' && 'text-red-700 dark:text-red-400')}>{value}</div>
      {sub && <div className="mt-1 text-xs text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

/** Lista chave/valor para fichas e resumos. */
export function KeyValues({ items, cols = 2 }: { items: [ReactNode, ReactNode][]; cols?: 1 | 2 | 3 }) {
  return (
    <dl className={clsx('grid gap-x-6 gap-y-3 text-sm', cols === 1 ? 'grid-cols-1' : cols === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
      {items.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v], i) => (
        <div key={i} className="min-w-0"><dt className="text-xs text-[var(--muted)]">{k}</dt><dd className="mt-0.5 font-medium break-words">{v}</dd></div>
      ))}
    </dl>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('size-5 animate-spin text-brand-600', className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-md bg-[var(--surface-2)]', className)} />;
}

export function Loading({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-3 py-2" aria-busy="true" aria-label="A carregar">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-9" />)}
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 py-14 text-center">
      {icon && <div className="mb-4 rounded-2xl bg-brand-500/10 p-3.5 text-brand-700 ring-8 ring-brand-500/5 dark:text-brand-500 [&>svg]:size-6">{icon}</div>}
      <p className="font-semibold">{title}</p>
      {children && <div className="mt-1 max-w-sm text-sm text-[var(--muted)]">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Alert({ tone = 'info', title, children, action }: { tone?: 'info' | 'warn' | 'error' | 'success'; title?: string; children: ReactNode; action?: ReactNode }) {
  const map = {
    info: ['border-blue-500/25 bg-blue-500/8 text-blue-900 dark:text-blue-200', Info],
    warn: ['border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200', AlertTriangle],
    error: ['border-red-500/30 bg-red-500/8 text-red-900 dark:text-red-200', XCircle],
    success: ['border-emerald-500/30 bg-emerald-500/8 text-emerald-900 dark:text-emerald-200', CheckCircle2],
  } as const;
  const [cls, Icon] = map[tone];
  return (
    <div className={clsx('flex gap-3 rounded-xl border px-4 py-3 text-sm', cls)} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="flex-1">{title && <div className="font-semibold">{title}</div>}<div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div></div>
      {action}
    </div>
  );
}

// ═══ Tabelas ════════════════════════════════════════════════════════════════
export function Table({ children, compact }: { children: ReactNode; compact?: boolean }) {
  return <div className="overflow-x-auto"><table className={clsx('w-full text-sm', !compact && 'min-w-[640px]')}>{children}</table></div>;
}
export const Th = ({ children, className, onClick, sorted }: { children?: ReactNode; className?: string; onClick?: () => void; sorted?: 'asc' | 'desc' | null }) => (
  <th className={clsx('border-b border-[var(--border)] bg-[var(--surface-2)] px-3 py-2.5 text-left text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase first:pl-5 last:pr-5', onClick && 'cursor-pointer select-none hover:text-[var(--text)]', className)}
    onClick={onClick} aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}>
    {children}{sorted && <ChevronDown className={clsx('ml-1 inline size-3', sorted === 'asc' && 'rotate-180')} />}
  </th>
);
export const Td = ({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) => (
  <td colSpan={colSpan} className={clsx('border-b border-[var(--border)] px-3 py-3 first:pl-5 last:pr-5', className)}>{children}</td>
);
export const Tr = ({ children, onClick, className }: { children: ReactNode; onClick?: () => void; className?: string }) => (
  <tr onClick={onClick} className={clsx('transition-colors', onClick && 'cursor-pointer hover:bg-brand-500/[0.04]', className)}>{children}</tr>
);

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between px-5 py-3 text-sm text-[var(--muted)]">
      <span className="tabular-nums">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total}</span>
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Anterior</Button>
          <span className="tabular-nums">{page} / {pages}</span>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Seguinte</Button>
        </div>
      )}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return <Input className={className} leading={<Search className="size-4" />} placeholder={placeholder ?? 'Pesquisar…'} value={value} onChange={(e) => onChange(e.target.value)} type="search" aria-label={placeholder ?? 'Pesquisar'} />;
}

/** Barra de ferramentas por cima de uma tabela, dentro de um Card sem padding. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] px-5 py-3">{children}</div>;
}

// ═══ Separadores e filtros ══════════════════════════════════════════════════
export function Tabs<T extends string>({ tabs, value, onChange, className }: { tabs: { id: T; label: ReactNode; count?: number; hidden?: boolean }[]; value: T; onChange: (v: T) => void; className?: string }) {
  return (
    <div role="tablist" className={clsx('flex gap-1 overflow-x-auto overflow-y-hidden border-b border-[var(--border)] [scrollbar-width:none]', className)}>
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.id} role="tab" type="button" aria-selected={value === t.id} onClick={() => onChange(t.id)}
          className={clsx('-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition',
            value === t.id ? 'border-brand-600 text-brand-800 dark:text-brand-100' : 'border-transparent text-[var(--muted)] hover:text-[var(--text)]')}>
          {t.label}
          {t.count !== undefined && <span className={clsx('rounded-full px-1.5 text-[11px] tabular-nums', value === t.id ? 'bg-brand-600 text-white' : 'bg-[var(--surface-2)]')}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({ options, value, onChange }: { options: { id: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup">
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} onClick={() => onChange(o.id)}
          className={clsx('rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition',
            value === o.id ? 'bg-brand-700 text-white ring-brand-700' : 'bg-[var(--surface)] text-[var(--text-2)] ring-[var(--border-strong)] hover:bg-[var(--surface-2)]')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ═══ Menu suspenso ══════════════════════════════════════════════════════════
export type MenuItem = { label: ReactNode; icon?: ReactNode; onClick: () => void; danger?: boolean; hidden?: boolean } | 'sep';
export function Dropdown({ trigger, items, align = 'right' }: { trigger: ReactElement; items: MenuItem[]; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const visible = items.filter((i) => i === 'sep' || !i.hidden);
  return (
    <div ref={ref} className="relative inline-block">
      {cloneElement(trigger as ReactElement<{ onClick?: () => void; 'aria-expanded'?: boolean; 'aria-haspopup'?: string }>, { onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open && (
        <div role="menu" className={clsx('surface absolute z-40 mt-1.5 min-w-56 rounded-xl p-1 shadow-xl shadow-black/10', align === 'right' ? 'right-0' : 'left-0')}>
          {visible.map((it, i) => it === 'sep' ? <div key={i} className="my-1 border-t border-[var(--border)]" /> : (
            <button key={i} role="menuitem" type="button" onClick={() => { setOpen(false); it.onClick(); }}
              className={clsx('flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition', it.danger ? 'text-red-600 hover:bg-red-500/10' : 'hover:bg-[var(--surface-2)]')}>
              {it.icon && <span className="text-[var(--muted)] [&>svg]:size-4">{it.icon}</span>}{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Modal (elemento <dialog> nativo: foco, Esc e camada de topo resolvidos) ═
export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md', icon }: {
  open: boolean; onClose: () => void; title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl'; icon?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  const w = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size];
  return (
    <dialog ref={ref} onClose={onClose} onMouseDown={(e) => e.target === ref.current && onClose()}
      className={clsx('surface m-auto w-[calc(100%-1.5rem)] overflow-hidden rounded-2xl p-0 text-[var(--text)] shadow-2xl', w)}>
      {open && (
        <div className="flex max-h-[min(88vh,900px)] flex-col">
          <header className="flex items-start gap-3 border-b border-[var(--border)] px-6 py-4">
            {icon && <div className="rounded-xl bg-brand-500/10 p-2 text-brand-700 dark:text-brand-500 [&>svg]:size-5">{icon}</div>}
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold">{title}</h2>
              {subtitle && <p className="mt-0.5 text-sm text-[var(--muted)]">{subtitle}</p>}
            </div>
            <Button variant="ghost" size="sm" iconOnly icon={<X className="size-4" />} onClick={onClose} aria-label="Fechar" />
          </header>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-6 py-3.5">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}

// ═══ Confirmação (substitui window.confirm/prompt) ══════════════════════════
interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  /** Pede um texto (ex.: motivo de anulação); devolve-o em vez de `true`. */
  input?: { label: string; placeholder?: string; minLength?: number };
}
type ConfirmFn = (o: ConfirmOptions) => Promise<string | true | null>;
const ConfirmCtx = createContext<ConfirmFn>(async () => null);
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: string | true | null) => void }) | null>(null);
  const [text, setText] = useState('');
  const confirm = useCallback<ConfirmFn>((o) => new Promise((resolve) => { setText(''); setState({ ...o, resolve }); }), []);
  const close = (v: string | true | null) => { state?.resolve(v); setState(null); };
  const min = state?.input?.minLength ?? 0;
  const valid = !state?.input || text.trim().length >= Math.max(min, 1);
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal open={!!state} onClose={() => close(null)} title={state?.title ?? ''} size="sm"
        icon={state?.tone === 'danger' ? <AlertTriangle className="text-red-600" /> : undefined}
        footer={<>
          <Button variant="secondary" onClick={() => close(null)}>Cancelar</Button>
          <Button variant={state?.tone === 'danger' ? 'danger' : 'primary'} disabled={!valid} onClick={() => close(state?.input ? text.trim() : true)}>
            {state?.confirmLabel ?? 'Confirmar'}
          </Button>
        </>}>
        {state?.description && <div className="text-sm leading-relaxed text-[var(--text-2)]">{state.description}</div>}
        {state?.input && (
          <Field label={state.input.label} className={state.description ? 'mt-4' : ''} hint={min > 1 ? `Mínimo ${min} caracteres` : undefined}>
            <Textarea autoFocus placeholder={state.input.placeholder} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        )}
      </Modal>
    </ConfirmCtx.Provider>
  );
}

// ═══ Notificações ═══════════════════════════════════════════════════════════
type Toast = { id: number; tone: 'ok' | 'error'; text: string };
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s.slice(-3), { id, tone, text }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), tone === 'error' ? 7000 : 3500);
  }, []);
  // Os modais são <dialog> (camada superior do browser): nenhum z-index os
  // ultrapassa e os avisos ficavam por trás do fundo desfocado, ilegíveis.
  // A pilha de avisos é um popover — mostrá-lo de novo põe-no por cima de tudo.
  const stack = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stack.current as (HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }) | null;
    if (!el?.showPopover) return;
    try {
      if (el.matches(':popover-open')) el.hidePopover!();
      if (items.length) el.showPopover();
    } catch { /* browser sem popover: fica o z-index */ }
  }, [items]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div ref={stack} popover="manual" className="fixed top-auto left-auto right-4 bottom-4 z-[60] m-0 flex w-[min(24rem,calc(100%-2rem))] flex-col gap-2 overflow-visible border-0 bg-transparent p-0 [&:not(:popover-open)]:hidden" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={clsx('flex items-start gap-3 rounded-xl px-4 py-3 text-sm shadow-xl ring-1',
            t.tone === 'ok' ? 'bg-gray-900 text-white ring-white/10 dark:bg-white dark:text-gray-900' : 'bg-red-600 text-white ring-red-700')}>
            {t.tone === 'ok' ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-400 dark:text-emerald-600" /> : <XCircle className="mt-0.5 size-4 shrink-0" />}
            <span className="flex-1">{t.text}</span>
            <button className="opacity-60 hover:opacity-100" aria-label="Fechar notificação" onClick={() => setItems((s) => s.filter((x) => x.id !== t.id))}><X className="size-4" /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** Mensagem legível de qualquer erro (a API já responde em português). */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.issues.length ? e.issues.map((i) => i.message).join(' · ') : e.message;
  return e instanceof Error ? e.message : 'Ocorreu um erro';
}

export function fieldErrors(e: unknown): Record<string, string> {
  if (!(e instanceof ApiError)) return {};
  return Object.fromEntries(e.issues.map((i) => [i.path, i.message]));
}
